import type { RecordId, Constructor } from '../types';
import { type fabric } from 'fabric';
import trackEvent from '../utils/analytics';
import {
  hasFrameBehaviors,
  hasFrameMemberBehaviors,
  hasLayout,
  isSimpleSelection,
  isSimpleGroup,
} from './guards';
import type { IPoint } from 'fabric/fabric-impl';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import { type MoveEvent } from '../external-types';
import type FrameBehaviors from './FrameBehaviors';
import type LayoutBehaviors from './LayoutBehaviors';

// Behaviors for objects which can be controlled by a frame. Frame member
// classes must include `container` in their `serializedKeys` and must call
// `initializeFrameMember` in their constructor.
//
// I imagine that one day this could be generalized to support containment
// so we could implement a table that contains other objects. This is
// why the serialized property is called 'container' and not 'frame'.
interface FrameMemberBehaviors {
  initializeFrameMember(): void;
  isDescendant(frame: FrameMemberBehaviors & fabric.Object): boolean;
  hasAncestor(frameId: string): boolean;
  container: RecordId | null;
  layoutPosition?: string | null;
  layoutManual?: boolean;
  layoutGapBefore?: number;
  layoutGapAfter?: number;
  /** Set the provided frame as this object's parent, if it is valid */
  setParentFrame(frame?: (FrameBehaviors & fabric.Object) | undefined): void;
  /**
   * Checks if this object allows itself to be added to the frame.
   * Called while dragging to test if we can be added to the Frame under the cursor.
   * @param frame The frame we want to check
   * @return boolean True if we can be added, false if not
   */
  isFrameAllowed(frame?: FrameBehaviors & fabric.Object): boolean;
  /** Find the object's containing frame, if there is a valid one, and set
   * it as the parent. May be passed an array of fabric objects to exclude
   * when determining the parent frame */
  maybeSetParentFrame(exclude?: fabric.Object[]): void;
  inFrame(): (FrameBehaviors & fabric.Object) | undefined;
  currentLayoutContainer: RecordId | null;
  frameMemberMoving: boolean;
  pointerOverFrame(
    pointer: IPoint
  ): (FrameBehaviors & fabric.Object) | undefined;

  // Update opacity when dragging over a frame
  updateDragOpacity(
    targetFrame?: (FrameBehaviors & fabric.Object) | undefined
  ): void;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
function FrameMemberBehaviors(Base: Constructor<fabric.Object>): any {
  return class extends Base implements FrameMemberBehaviors {
    __hasFrameMemberBehaviors = true;
    // The frame the mouse is currently overing over.
    _overFrame: (FrameBehaviors & fabric.Object) | null;
    // The ID of the frame this object is inside.
    container: RecordId | null;
    // The position of the object in a layout.
    layoutPosition?: string | null;
    // Whether the object should ignore auto-layout.
    layoutManual?: boolean;
    // Individual gap spacing before this object in layout direction.
    layoutGapBefore?: number;
    // Individual gap spacing after this object in layout direction.
    layoutGapAfter?: number;
    // The layout this object is currently in.
    currentLayoutContainer: RecordId | null;
    // Store the original opacity when temporarily changing it during drag
    private originalOpacity?: number;
    // Manually track moving in order to avoid triggering layout after moving is
    // done without reassigning frames on click. Named to avoid clash with fabric isMoving
    frameMemberMoving: boolean;

    // Register for events we care about.
    initializeFrameMember() {
      this.on('moved', (e: MoveEvent) => {
        if (this.frameMemberMoving) {
          const targetFrame = this.getTargetFrame(e);
          targetFrame?.fire('member:moved', {
            target: this,
            currentTarget: targetFrame,
          });
        }
      });
      this.on('moving', (e: MoveEvent) => {
        this.frameMemberMoving = true;
        // Highlight any frames the object is dragged over.
        if (!e.frameMoving) {
          // e might be a fake event that doesn't have pointer info, if so we can't determine
          // the parent frame if any, so just do nothing
          if (!e.pointer) return;

          const currentFrame = this.inFrame();
          const targetFrame = this.getTargetFrame(e);

          // Clear current layout id if we are over a different frame than the
          // current layout (or its descendants)
          if (
            this.currentLayoutContainer &&
            targetFrame &&
            targetFrame.id !== this.currentLayoutContainer
          ) {
            this.currentLayoutContainer = null;
          }

          // Skip frame interactions if needed
          if (targetFrame) {
            let canAddToFrame = this.canAddToFrame
              ? this.canAddToFrame(targetFrame)
              : true;
            canAddToFrame &&= targetFrame.canAddFrameMember
              ? targetFrame.canAddFrameMember(this)
              : true;
            if (!canAddToFrame) {
              return;
            }
          }

          if (!this.selectionContains(targetFrame)) {
            this.highlightFrame(targetFrame, e);
            this.handleDragOpacity(targetFrame);
          }

          // Update this object's container to the frame the pointer is over. This
          // will give us the "preview" behavior for layouts in the new frame
          if (
            !this.currentLayoutContainer &&
            targetFrame &&
            targetFrame !== currentFrame
          ) {
            if (!this.selectionContains(targetFrame)) {
              this.setParentFrame(targetFrame);
            }
          }

          // Handle sorting in Layouts.
          if (e.pointer && targetFrame && hasLayout(targetFrame)) {
            if (isSimpleSelection(this)) {
              this.forEach(
                obj =>
                  e.pointer &&
                  targetFrame.positionMember(obj, e.pointer.x, e.pointer.y)
              );
            } else {
              targetFrame.positionMember(this, e.pointer.x, e.pointer.y);
            }
          }

          // Immediately remove from the frame if we are no longer over a frame
          if (!isSimpleSelection(this) && !targetFrame) {
            this.setParentFrame(undefined);
          }
        }
      });
      this.on('movedPoint', () => {
        this.maybeSetParentFrame();
      });
      this.on('deleted', () => this.maybeLayoutFrame());
      // When a group is created remove the objects from the frame since the
      // group will be added instead.
      this.on(
        'group:added',
        () =>
          this.simpleGroup &&
          isSimpleGroup(this.simpleGroup) &&
          this.setParentFrame(undefined)
      );
      this.on('group:removed', () => {
        this.maybeSetParentFrame();
        this.maybeLayoutFrame();
      });

      this.on('mousedown', () => {
        const frame = this.inFrame();
        if (frame && hasLayout(frame)) {
          this.currentLayoutContainer = this.container;
        }
      });

      this.on('mouseup', e => {
        // Clear indicator for original frame
        this.clearFrameIndicators();

        // Clear currentLayoutContainer for this object
        this.currentLayoutContainer = null;

        // Skip if we're not allowed to be added to the frame
        let targetFrame = this.getTargetFrame(e.e);
        if (this.isFrameAllowed(targetFrame)) {
          // Reassign frame member if the member was moving prior to mouseup
          if (
            this.frameMemberMoving &&
            // Dont reassign if 'this' is a simple selection containing the target frame
            (!isSimpleSelection(this) || !this.objectInSelection(targetFrame))
          ) {
            const updateFrameMemberInLayout = object => {
              if (
                isSimpleSelection(object) &&
                !object.objectInSelection(targetFrame) &&
                targetFrame
              ) {
                object.forEach(obj => {
                  if (hasFrameMemberBehaviors(obj)) {
                    updateFrameMemberInLayout(obj);
                  }
                });
                return;
              }

              object.setParentFrame(targetFrame);
              object.maybeLayoutFrame(true);
            };

            updateFrameMemberInLayout(this);
          }
        } else {
          targetFrame = this.inFrame();
          if (targetFrame && hasLayout(targetFrame)) {
            targetFrame.layout();
          }
        }

        this.updateDragOpacity();

        this._overFrame?.clearHovered();
        this._overFrame = null;
        this.frameMemberMoving = false;
      });

      this.setUndoCompletionEvents(['mouseup', 'scaled', 'rotated', 'skewed']);
    }

    clearFrameIndicators() {
      const objects = isSimpleSelection(this) ? this._objects : [this];

      for (const obj of objects) {
        if (!hasFrameMemberBehaviors(obj)) continue;
        if (obj.container) {
          const frame = obj.inFrame();
          if (frame && hasLayout(frame)) {
            frame.clearIndicatorObject();
          }
        }
      }
    }

    isFrameAllowed(frame?: (FrameBehaviors & fabric.Object) | null): boolean {
      let canAddToFrame = this.canAddToFrame ? this.canAddToFrame(frame) : true;
      if (frame) {
        canAddToFrame &&= frame.canAddFrameMember
          ? frame.canAddFrameMember(this)
          : true;
      } else {
        // Does existing frame allow removal
        const existingFrame = this.inFrame();
        if (existingFrame) {
          canAddToFrame &&= existingFrame.canRemoveFrameMember
            ? existingFrame.canRemoveFrameMember(this)
            : true;
        }
      }
      return canAddToFrame;
    }

    maybeLayoutFrame(updateAll = false) {
      const containerFrame = this.inFrame();
      if (containerFrame && hasLayout(containerFrame)) {
        containerFrame.layout(updateAll);
      }
    }

    maybeSetParentFrame(exclude?: fabric.Object[]) {
      const frame = this.overFrame(exclude);
      if (frame) {
        this.setParentFrame(frame);
      }
    }

    setParentFrame(frame: (FrameBehaviors & fabric.Object) | undefined) {
      // For selections, process each object individually.
      if (isSimpleSelection(this)) {
        this.forEach(
          obj => hasFrameMemberBehaviors(obj) && obj.setParentFrame(frame)
        );
        return;
      }

      // Skip adding MindMap objects to frames
      if (!this.isFrameAllowed(frame)) {
        return;
      }

      const isAncestor =
        hasFrameBehaviors(this) &&
        hasFrameMemberBehaviors(frame) &&
        frame?.hasAncestor(this.id);

      if (
        frame &&
        this.container !== frame.id &&
        !isAncestor &&
        this.id !== frame.id
      ) {
        frame.addMember(this);

        if (frame.type === 'table') {
          trackEvent(
            'Feature-7176742954015814903',
            'User adds objects to whiteboard table'
          );
        }
      } else if (!frame && this.container) {
        const container = this.currentLayoutContainer
          ? this.currentLayoutContainer
          : null;
        this.set({ container, layoutPosition: null });
        this.canvas.collectionNeedsSort = true;
      }

      this._overFrame?.clearHovered();
      this._overFrame = null;
    }

    // Show a visible indicator where the object will be contained.
    highlightFrame(frame: (FrameBehaviors & fabric.Object) | undefined, e) {
      if (frame) {
        // If we move from an overlapping frame, clear that frame's hover
        this._overFrame?.clearHovered();
        this._overFrame = frame;
        this._overFrame.setHovered(e);
      } else {
        this._overFrame?.clearHovered();
        this._overFrame = null;
      }
    }

    // Return the frame the object is contained by.
    overFrame(
      exclude?: fabric.Object[]
    ): (FrameBehaviors & fabric.Object) | undefined {
      // Topmost frame gets the object
      const allFrames = this.canvas
        .getObjects()
        .filter(hasFrameBehaviors)
        .filter(
          frame => frame.frameBehaviorsEnabled && !exclude?.includes(frame)
        );
      for (let i = allFrames.length - 1; i >= 0; i--) {
        const frame = allFrames[i];
        if (
          frame.id !== this.id &&
          (!this.id ||
            !hasFrameMemberBehaviors(frame) ||
            frame.container !== this.id) &&
          frame.containsObject(this)
        ) {
          return frame;
        }
      }
    }

    // Return the frame the pointer is over.
    pointerOverFrame(
      pointer: IPoint
    ): (FrameBehaviors & fabric.Object) | undefined {
      const allFrames = this.canvas
        .getObjects()
        .filter(obj => hasFrameBehaviors(obj) && obj.frameBehaviorsEnabled);

      for (let i = allFrames.length - 1; i >= 0; i--) {
        const frame = allFrames[i];
        if (
          hasFrameMemberBehaviors(frame) &&
          hasFrameBehaviors(frame) &&
          frame.id !== this.id &&
          (!this.id || frame.container !== this.id) &&
          frame.frameContainsPoint(pointer)
        ) {
          return frame;
        }
      }
    }

    // Check if this object is a descendant of a given frame
    isDescendant(frame: FrameMemberBehaviors & fabric.Object) {
      let object: fabric.Object = this;
      while (hasFrameMemberBehaviors(object) && object?.container) {
        if (object.container === frame.id) return true;
        object = this.controller.dataStoreObjects.findRecordById(
          object.container
        );
      }
      return false;
    }

    hasAncestor(frameId: string) {
      const ancestorIds = new Set<string>();
      let object: fabric.Object = this;

      // Have to be careful that we don't get into an infinite loop if there is
      // a cycle in the container hierarchy. Situations like this can be
      // possible temporarily, possibly due to collaboration.
      while (
        object &&
        hasFrameMemberBehaviors(object) &&
        object.container &&
        !ancestorIds.has(object.container)
      ) {
        if (object.container === frameId) return true;

        ancestorIds.add(object.container);
        object = this.controller.dataStoreObjects.findRecordById(
          object.container
        );
      }

      return false;
    }

    // Return the frame this member is in.
    inFrame(): (FrameBehaviors & fabric.Object) | undefined {
      if (this.container) {
        const possibleFrame = this.controller.dataStoreObjects.findRecordById(
          this.container
        );
        if (hasFrameBehaviors(possibleFrame)) {
          return possibleFrame as FrameBehaviors & fabric.Object;
        }
      }
    }

    _set(keyOrPath: KeyPath, value: any): fabric.Object {
      let oldContainer: RecordId | null = null;
      if (keyOrPath === 'container') {
        oldContainer = this.container;
      }

      super._set(keyOrPath, value);

      // Re-layout the old frame if the container changed.
      if (
        oldContainer &&
        this.container !== oldContainer &&
        keyOrPath === 'container'
      ) {
        const oldFrame =
          this.controller.dataStoreObjects.findRecordById(oldContainer);
        if (oldFrame && hasLayout(oldFrame)) {
          oldFrame.layout();
          oldFrame.clearIndicatorObject();
        }
      }

      if (this.container) {
        if (keyOrPath === 'layoutPosition' && this.objectIsMoving()) {
          // If we are moving around an object already in a container, then we
          // use WYSIWYG repositioning amongst siblings
          if (this.currentLayoutContainer === this.container) {
            this.maybeLayoutFrame();
          } else {
            // Otherwise, if we are adding an object to a new frame, we use an
            // indicator to show where the object will be added to the new frame.
            // This prevents the objects from jumping around, which is important
            // for nested (and potentially dynamically sizing) layouts.
            const frame = this.inFrame();
            if (frame && hasLayout(frame)) {
              frame.setIndicatorObject(this);
            }
          }
        }
      }

      return this;
    }

    // Finds the frame this object is over. We favor the frame underneath the user's
    // cursor. We also check if the object is already in a layout, in which case
    // we avoid adding to nested layouts
    private getTargetFrame(
      e: Event
    ): (FrameBehaviors & fabric.Object) | undefined {
      const pointerFrame = this.pointerOverFrame(
        this.canvas.getPointer(e, true)
      );

      let targetFrame = pointerFrame;
      const layoutFrame = this.getLayoutFrame();

      if (layoutFrame && targetFrame) {
        if (targetFrame.isInFrameHierarchy(targetFrame, layoutFrame)) {
          targetFrame = layoutFrame;
        }
      }
      return targetFrame;
    }

    // While this does not explicitly check against layout frames here, currentLayoutContainer
    // is only set if the frame is a layout frame
    private getLayoutFrame():
      | (LayoutBehaviors & FrameBehaviors & fabric.Object)
      | undefined {
      return this.currentLayoutContainer
        ? this.canvas
            .getObjects()
            .filter(obj => hasFrameBehaviors(obj) && hasLayout(obj))
            .find(obj => obj.id === this.currentLayoutContainer)
        : undefined;
    }

    private selectionContains(
      frame?: (FrameBehaviors & fabric.Object) | undefined
    ): boolean {
      if (!frame) {
        return false;
      }

      const selection = this.canvas?.getActiveObject();
      if (!selection) {
        return false;
      }

      if (isSimpleSelection(selection)) {
        return selection
          .getObjects()
          .some(
            obj =>
              (hasFrameMemberBehaviors(obj) && frame.isDescendant(obj)) ||
              obj.id === frame.id
          );
      } else {
        const selectedObject = selection;
        if (hasFrameMemberBehaviors(selectedObject)) {
          return (
            frame.isDescendant(selectedObject) || selectedObject.id === frame.id
          );
        }
        return selectedObject.id === frame.id;
      }
    }

    handleDragOpacity(
      targetFrame: (FrameBehaviors & fabric.Object) | undefined
    ) {
      // Skip for objects in simple groups since groups will call transformObjects
      // which does not pass down the pointer event
      if (this.simpleGroup) return;

      // Apply opacity based on whether there's a target frame
      this.updateDragOpacity(targetFrame);
    }

    updateDragOpacity(targetFrame?: FrameBehaviors & fabric.Object) {
      if (isSimpleSelection(this) || isSimpleGroup(this)) {
        // Handle collections recursively
        this.forEach(obj => {
          if (hasFrameMemberBehaviors(obj)) {
            obj.updateDragOpacity(targetFrame);
          }
        });
      } else {
        if (targetFrame) {
          // Save original and set reduced opacity
          if (!this.originalOpacity) {
            this.originalOpacity = this.opacity;
            this.opacity = 0.5;
          }
        } else {
          // Restore original opacity
          if (this.originalOpacity) {
            this.opacity = this.originalOpacity;
            this.originalOpacity = undefined;
          }
        }
      }
    }
  };
}

export default FrameMemberBehaviors;
